import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { fmtCount } from '../../lib/connectedBusinesses';

/* Shared primitives for the Bakemill ERP card */

export function Kpi({ label, value, danger, sub, testId }) {
  return (
    <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-xl font-extrabold tabular-nums mt-1 ${danger ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export function Collapsible({ title, count, defaultOpen = false, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-gray-100" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-bold text-gray-800 hover:bg-gray-50 rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
      >
        <span>{title}{count != null && <span className="ml-2 text-xs font-semibold text-gray-400">({count})</span>}</span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-2 pb-2">{children}</div>}
    </div>
  );
}

/** columns: [{ key, label, align?, render? }] */
export function MiniTable({ columns, rows, empty = 'Nothing to show.', footer, testId }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
            {columns.map(c => <th key={c.key} className={`py-2 px-3 font-bold ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="py-6 text-center text-gray-400 text-xs">{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/60">
              {columns.map(c => (
                <td key={c.key} className={`py-2 px-3 ${c.align === 'right' ? 'text-right tabular-nums' : 'text-left'} text-gray-700`}>
                  {c.render ? c.render(r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && rows.length > 0 && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
}

function SectionShell({ title, testId, children }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="pt-5 border-t border-gray-100" data-testid={testId}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} data-testid={`${testId}-toggle`}
        className="flex items-center gap-2 mb-3 text-[11px] font-extrabold uppercase tracking-widest text-gray-500 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 rounded">
        {title}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="space-y-3">{children}</div>}
    </section>
  );
}

/* Section A — ERP */
export function ErpSection({ erp, money }) {
  const m = (v) => (v == null ? '—' : money(v));
  return (
    <div className="space-y-3" data-testid="bakemill-erp-section">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Today's Total Sales" value={m(erp.todaySales)} testId="bakemill-kpi-today-sales" />
        <Kpi label="Monthly Revenue" value={m(erp.monthlyRevenue)} testId="bakemill-kpi-monthly-revenue" />
        <Kpi label="Pending Purchase Orders" value={fmtCount(erp.pendingPOs)} testId="bakemill-kpi-pending-pos" />
        <Kpi label="Low Stock Alerts" value={fmtCount(erp.lowStockCount)} danger={erp.lowStockCount > 0} testId="bakemill-kpi-low-stock" />
      </div>
      <Collapsible title="Top Selling Products" count={erp.topProducts.length} testId="bakemill-top-products">
        <MiniTable testId="bakemill-top-products-table" rows={erp.topProducts} empty="No sales data."
          columns={[
            { key: 'name', label: 'Product' },
            { key: 'qty', label: 'Qty Sold', align: 'right', render: r => fmtCount(r.qty) },
            { key: 'revenue', label: 'Revenue', align: 'right', render: r => m(r.revenue) },
          ]} />
      </Collapsible>
      <Collapsible title="Low Stock Items" count={erp.lowStock.length} testId="bakemill-low-stock">
        <MiniTable testId="bakemill-low-stock-table" rows={erp.lowStock} empty="No low stock items."
          columns={[
            { key: 'sku', label: 'SKU' },
            { key: 'name', label: 'Name' },
            { key: 'qty', label: 'Qty / Threshold', align: 'right', render: r => `${fmtCount(r.qty)} / ${fmtCount(r.threshold)}` },
          ]} />
      </Collapsible>
    </div>
  );
}

/* Section B — Retail Showrooms */
const sum = (rows, k) => {
  const vals = rows.map(r => r[k]).filter(v => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
};

export function ShowroomsSection({ showrooms, money }) {
  const m = (v) => (v == null ? '—' : money(v));
  const rows = showrooms || [];
  const pct = (v) => (v == null ? '' : ` (${v}%)`);
  return (
    <SectionShell title="Retail Showrooms" testId="bakemill-showrooms-section">
      {showrooms == null ? <p className="text-sm text-gray-400">—</p> : (
        <MiniTable testId="bakemill-showrooms-table" rows={rows} empty="No showrooms."
          columns={[
            { key: 'name', label: 'Name', render: r => <span><span className="font-semibold text-gray-900">{r.name}</span>{r.city && <span className="text-gray-400 text-xs"> · {r.city}</span>}</span> },
            { key: 'todaySales', label: "Today's Sales", align: 'right', render: r => m(r.todaySales) },
            { key: 'monthlyRevenue', label: 'Monthly Revenue', align: 'right', render: r => m(r.monthlyRevenue) },
            { key: 'stockAlerts', label: 'Stock Alerts', align: 'right', render: r => <span className={r.stockAlerts > 0 ? 'text-red-600 font-bold' : ''}>{fmtCount(r.stockAlerts)}</span> },
            { key: 'ownerSplit', label: 'Owner Split', align: 'right', render: r => `${m(r.ownerSplit)}${r.ownerSplit != null ? pct(r.ownerPct) : ''}` },
            { key: 'franchiseeSplit', label: 'Franchisee Split', align: 'right', render: r => `${m(r.franchiseeSplit)}${r.franchiseeSplit != null ? pct(r.franchiseePct) : ''}` },
          ]}
          footer={
            <tr className="bg-gray-50 font-bold text-gray-900 text-sm" data-testid="bakemill-showrooms-totals">
              <td className="py-2 px-3">Total</td>
              <td className="py-2 px-3 text-right tabular-nums">{m(sum(rows, 'todaySales'))}</td>
              <td className="py-2 px-3 text-right tabular-nums">{m(sum(rows, 'monthlyRevenue'))}</td>
              <td className="py-2 px-3 text-right tabular-nums">{fmtCount(sum(rows, 'stockAlerts'))}</td>
              <td className="py-2 px-3 text-right tabular-nums">{m(sum(rows, 'ownerSplit'))}</td>
              <td className="py-2 px-3 text-right tabular-nums">{m(sum(rows, 'franchiseeSplit'))}</td>
            </tr>
          } />
      )}
    </SectionShell>
  );
}

/* Section C — Manufacturing */
export function MfgSection({ mfg }) {
  return (
    <SectionShell title="Manufacturing" testId="bakemill-mfg-section">
      {!mfg ? <p className="text-sm text-gray-400">—</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Active Production Orders" value={fmtCount(mfg.active)} testId="bakemill-kpi-active-prod" />
            <Kpi label="Produced Today" value={fmtCount(mfg.producedToday)} sub={mfg.producedBatches != null ? `${fmtCount(mfg.producedBatches)} batches` : null} testId="bakemill-kpi-produced-today" />
            <Kpi label="Raw Material Alerts" value={fmtCount(mfg.rawAlertsCount)} danger={mfg.rawAlertsCount > 0} testId="bakemill-kpi-raw-alerts" />
            <Kpi label="Pending Dispatches" value={fmtCount(mfg.pendingDispatch)} testId="bakemill-kpi-pending-dispatch" />
          </div>
          <Collapsible title="Raw Material Alerts" count={mfg.rawAlerts.length} testId="bakemill-raw-alerts">
            <MiniTable testId="bakemill-raw-alerts-table" rows={mfg.rawAlerts} empty="No raw material alerts."
              columns={[{ key: 'name', label: 'Name' }, { key: 'qty', label: 'Qty', align: 'right', render: r => fmtCount(r.qty) }]} />
          </Collapsible>
        </>
      )}
    </SectionShell>
  );
}
